import React, { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePendingApprovals } from '../../contexts/PendingApprovalsContext';
import { usePendingStorefronts } from '../../contexts/PendingStorefrontsContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  NAVIGATION,
  NavSection,
  isRouteActive,
  sectionForPath,
} from './navigation';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * Two levels, one at a time: the root menu, or one section with a way back.
 *
 * The accordion this replaced put every group inline, so an admin holding
 * most permissions scrolled forty-three links in a single column and the
 * only grouping — a "System" bin of twenty-six — told them nothing. A
 * drill-down shows nine rows or one section's worth, never both.
 *
 * Which panel is showing is *derived from the URL*, not stored: land on
 * `/compliance/cases` from a notification and the Compliance section is
 * already open. `panel` only records a deliberate override — pressing back
 * to the root menu, or opening a section — and only for the path it was
 * pressed on, so the next navigation re-derives and the two can't drift.
 */
export const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const { admin, can } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { count: pendingApprovals } = usePendingApprovals();
  const { count: pendingStorefronts } = usePendingStorefronts();
  const [panel, setPanel] = useState<{ path: string; key: string | null } | null>(
    null,
  );

  // Sections and items the current admin may actually reach. A section with
  // nothing left in it disappears rather than opening onto an empty panel.
  const sections = useMemo(() => {
    return NAVIGATION.map((section) => ({
      ...section,
      items: section.items.filter((item) => can(item.permission)),
    })).filter((section) => section.items.length > 0);
    // admin.permissions drives access; re-run when the admin object changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.permissions, can]);

  const derivedKey = sectionForPath(location.pathname)?.key ?? null;
  const openKey =
    panel && panel.path === location.pathname ? panel.key : derivedKey;
  const openSection = sections.find((s) => s.key === openKey) ?? null;

  // Pending-count badges, shown only to admins who can act on the queue.
  const badgeFor = (to: string): number => {
    if (to === '/approvals') return can('approvals.review') ? pendingApprovals : 0;
    if (to === '/storefront-review') {
      return can('storefront.review') ? pendingStorefronts : 0;
    }
    return 0;
  };

  const sectionBadge = (section: NavSection) =>
    section.items.reduce((sum, item) => sum + badgeFor(item.to), 0);

  const countBadge = (count: number) =>
    count > 0 ? (
      <span
        style={{
          marginLeft: 'auto',
          background: 'var(--danger-600)',
          color: 'white',
          borderRadius: 'var(--radius-full)',
          fontSize: 11,
          fontWeight: 700,
          lineHeight: '16px',
          minWidth: 18,
          padding: '0 5px',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {count > 99 ? '99+' : count}
      </span>
    ) : null;

  const rowStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    marginBottom: 2,
    borderRadius: 'var(--radius-md)',
    color: isActive ? 'var(--blue-700)' : 'var(--neutral-700)',
    background: isActive ? 'var(--blue-100)' : 'transparent',
    fontSize: 14,
    fontWeight: isActive ? 600 : 500,
    textDecoration: 'none',
    transition: 'all 150ms ease',
    width: '100%',
    border: 0,
    textAlign: 'left',
  });

  // Opening a section lands on its first page as well as showing its menu —
  // one click to work, the same as the flat list cost.
  const openSectionAt = (section: NavSection) => {
    const first = section.items[0];
    setPanel({ path: first.to, key: section.key });
    navigate(first.to);
    onClose?.();
  };

  return (
    <aside
      className={`admin-sidebar${isOpen ? ' admin-sidebar--open' : ''}`}
      style={{
        width: 240,
        background: 'var(--neutral-100)',
        borderRight: '1px solid var(--neutral-300)',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--neutral-300)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              background: 'var(--blue-600)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            P
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)' }}>
              PayIQ
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--neutral-500)',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
              }}
            >
              Admin Console
            </div>
          </div>
        </div>
      </div>

      <div className="admin-nav-viewport">
        <div
          className="admin-nav-track"
          style={{ transform: openSection ? 'translateX(-50%)' : 'translateX(0)' }}
        >
          {/* Root menu */}
          <nav
            className="admin-nav-panel"
            aria-label="Main menu"
            aria-hidden={openSection ? true : undefined}
          >
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = section.key === derivedKey;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => openSectionAt(section)}
                  style={{ ...rowStyle(isActive), cursor: 'pointer' }}
                  tabIndex={openSection ? -1 : undefined}
                >
                  <Icon size={18} />
                  <span>{section.label}</span>
                  {countBadge(sectionBadge(section))}
                  <ChevronRight size={16} style={{ marginLeft: 'auto' }} />
                </button>
              );
            })}
          </nav>

          {/* One section */}
          <nav
            className="admin-nav-panel"
            aria-label={openSection?.label ?? 'Section menu'}
            aria-hidden={openSection ? undefined : true}
          >
            <button
              type="button"
              onClick={() => setPanel({ path: location.pathname, key: null })}
              style={{
                ...rowStyle(false),
                cursor: 'pointer',
                gap: 8,
                color: 'var(--neutral-500)',
                fontSize: 13,
                marginBottom: 8,
              }}
              tabIndex={openSection ? undefined : -1}
            >
              <ChevronLeft size={16} />
              <span>Main menu</span>
            </button>

            <div
              style={{
                padding: '0 14px 10px',
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--neutral-800)',
              }}
            >
              {openSection?.label}
            </div>

            {openSection?.items.map((item) => {
              const isActive = isRouteActive(location.pathname, item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  style={rowStyle(isActive)}
                >
                  <item.icon size={17} />
                  <span>{item.label}</span>
                  {countBadge(badgeFor(item.to))}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      <div
        style={{
          padding: 16,
          borderTop: '1px solid var(--neutral-300)',
          fontSize: 11,
          color: 'var(--neutral-500)',
          textAlign: 'center',
        }}
      >
        PayIQ Admin v1.0
      </div>
    </aside>
  );
};
